import React from 'react';
import { MenuItem, Select, TextField, Typography, Box } from '@mui/material';
import './VideoUpload.css';
import 'react-toastify/dist/ReactToastify.css';
import { ACM } from '../../constants/UserRoles';
import useUploadVideoState from './useUploadVideoState';
import { truncateName } from './videoUploadUtils';
import FileUploadSection from './FileUploadSection';
import GoalSelectionSection from './GoalSelectionSection';
import AccessPermissionsSection from './AccessPermissionsSection';
import ConfirmChangeDialog from './ConfirmChangeDialog';
import VideoUploadActions from './VideoUploadActions';
import BootstrapTooltip from './VideoUploadStyled';

const UploadVideo = ({ user, typeOfUser }) => {
  const state = useUploadVideoState(user, typeOfUser);

  return (
    <Box>
      <Typography gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
        Upload video for review
      </Typography>

      <FileUploadSection
        isLoading={state.isLoading}
        handleFileChange={state.handleFileChange}
        uploadedFile={state.uploadedFile}
        customFileName={state.customFileName}
        handleCustomFileNameChange={state.handleCustomFileNameChange}
        date={state.date}
        dateChangeHandler={state.dateChangeHandler}
      />
      {(ACM[typeOfUser]?.isTeacher || ACM[typeOfUser]?.isDoctor) && (
        <>
          <Typography gutterBottom>
            {ACM[typeOfUser]?.isTeacher ? 'Students List' : 'Patients List'}
          </Typography>
          <Select
            value={
              ACM[typeOfUser]?.isTeacher
                ? state.selectedStudent
                : state.selectedPatient
            }
            className='fullWidth'
            onChange={
              ACM[typeOfUser]?.isTeacher
                ? state.handleStudentSelection
                : state.handlePatientSelection
            }
            disabled={state.isLoading}
          >
            {(Array.isArray(state.mergedData) ? [...state.mergedData] : [])
              .sort((a, b) =>
                (a?.fullname || a?.name || '')
                  .toLowerCase()
                  .localeCompare((b?.fullname || b?.name || '').toLowerCase())
              )
              .map((item) => (
                <MenuItem key={item.id} value={item?.id}>
                  <BootstrapTooltip
                    title={
                      item?.parentName
                        ? `${item.fullname || item.name}(${item.parentName})`
                        : item?.fullname || item.name
                    }
                  >
                    <span>
                      {item?.parentName
                        ? `${truncateName(
                            item.fullname || item.name
                          )} (${truncateName(item.parentName)})`
                        : `${truncateName(item.fullname || item.name)}`}
                    </span>
                  </BootstrapTooltip>
                </MenuItem>
              ))}
          </Select>
        </>
      )}
      <Typography
        sx={{
          color: '#9291F0',
          fontFamily: 'Karla',
          fontWeight: 750,
          fontSize: '24px',
          borderBottom: '3px solid #9291F0',
          marginTop: '20px',
          marginBottom: '10px',
        }}
      ></Typography>

      <GoalSelectionSection
        isFirstSectionComplete={state.isFirstSectionComplete}
        goalAssociatedWithBehavior={state.goalAssociatedWithBehavior}
        handleGoalAssociatedWithBehavior={
          state.handleGoalAssociatedWithBehavior
        }
        selectedGoalIds={state.selectedGoalIds}
        handleGoalIdChange={state.handleGoalIdChange}
        goalsDropDown={state.goalsDropDown}
        selectCategory={state.selectCategory}
        handleSelectCategory={state.handleSelectCategory}
        selectedBehaviorIds={state.selectedBehaviorIds}
        handleBehaviorIdChange={state.handleBehaviorIdChange}
        behaviorsOnly={state.behaviorsOnly}
        selectedSkillIds={state.selectedSkillIds}
        handleSkillIdChange={state.handleSkillIdChange}
        skillsOnly={state.skillsOnly}
        otherInputValue={state.otherInputValue}
        setOtherInputValue={state.setOtherInputValue}
      />

      <Typography
        sx={{
          color: !state.isFirstSectionComplete ? 'gray' : '#9291F0',
          mt: 2,
        }}
        gutterBottom={state.isFirstSectionComplete}
      >
        * Add Therapy Type
      </Typography>
      <TextField
        fullWidth
        label='Therapy Type'
        value={state.therapyType}
        onChange={(e) => state.setTherapyType(e.target.value)}
        disabled={state.isLoading || !state.isFirstSectionComplete}
      />

      <Typography
        sx={{
          color: !state.isFirstSectionComplete ? 'gray' : '#9291F0',
          mt: 2,
        }}
        gutterBottom={state.isFirstSectionComplete}
      >
        * Add Interventions
      </Typography>
      <TextField
        fullWidth
        label='Interventions'
        value={state.interventions}
        onChange={(e) => state.setInterventions(e.target.value)}
        disabled={state.isLoading || !state.isFirstSectionComplete}
      />

      <Typography
        sx={{
          color: !state.isFirstSectionComplete ? 'gray' : '#9291F0',
          mt: 2,
        }}
        gutterBottom={state.isFirstSectionComplete}
      >
        * Antecedent
      </Typography>
      <TextField
        fullWidth
        label='Antecedent'
        value={state.antecedent}
        onChange={(e) => state.setAntecedent(e.target.value)}
        disabled={state.isLoading || !state.isFirstSectionComplete}
      />

      <Typography
        sx={{
          color: !state.isFirstSectionComplete ? 'gray' : '#9291F0',
          mt: 2,
        }}
        gutterBottom={state.isFirstSectionComplete}
      >
        * Consequence
      </Typography>
      <TextField
        fullWidth
        label='Consequence'
        value={state.consequence}
        onChange={(e) => state.setConsequence(e.target.value)}
        disabled={state.isLoading || !state.isFirstSectionComplete}
      />

      <Typography
        sx={{
          marginBottom: '8px',
          color: !state.isFirstSectionComplete ? 'gray' : '#9291F0',
        }}
        gutterBottom={state.isFirstSectionComplete}
      >
        * Description of events leading up to this behavior
      </Typography>
      <TextField
        className='notes fullWidth fullHeight'
        variant='outlined'
        size='medium'
        label='Please input description...'
        sx={{ my: 0.5 }}
        multiline
        rows={6}
        InputProps={{
          style: {
            minHeight: '55px', // Adjust the height as needed
          },
        }}
        value={state.information.notes}
        onChange={(e) =>
          state.setInformation({
            ...state.information,
            notes: e.target.value,
          })
        }
        // disabled={isLoading || !isPatientSelected}
        disabled={state.isLoading || !state.isFirstSectionComplete}
      />

      {(ACM[typeOfUser]?.isTeacher || ACM[typeOfUser]?.isDoctor) && (
        <AccessPermissionsSection
          videoFeedBackNotesPrivate={state.videoFeedBackNotesPrivate}
          setVideoFeedBackNotesPrivate={state.setVideoFeedBackNotesPrivate}
          peopleToShareWith={state.peopleToShareWith}
          editAndViewIds={state.editAndViewIds}
          setEditAndViewIds={state.setEditAndViewIds}
          viewOnlyIds={state.viewOnlyIds}
          setViewOnlyIds={state.setViewOnlyIds}
        />
      )}

      <VideoUploadActions
        handleClose={state.handleClose}
        isLoading={state.isLoading}
        videoSaveHandler={state.videoSaveHandler}
      />

      <ConfirmChangeDialog
        showDialog={state.showDialog}
        handleDialog={state.handleDialog}
        setIsResetConfirmed={state.setIsResetConfirmed}
        handleConfirmChange={state.handleConfirmChange}
      />
    </Box>
  );
};

export default UploadVideo;
